import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Newspaper, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/newsUtils";
import { ThemeToggle } from "@/components/ThemeToggle";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-background relative">
    <ThemeToggle className="absolute top-5 right-5 z-10" />
    <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-white flex items-center justify-center">
          <Newspaper className="w-5 h-5 text-primary" />
        </div>
        <span className="font-heading text-xl font-black tracking-tight">NEWS DIGEST</span>
      </div>
      <div>
        <h2 className="font-heading text-4xl xl:text-5xl font-black leading-[1.05] tracking-tight">
          Tus medios. Resumidos por IA. En un solo feed.
        </h2>
        <p className="mt-6 text-white/80 max-w-md text-base">
          Elige los medios que quieres vigilar y recibe cada noticia condensada con Gemini,
          lista para leer de un vistazo. Tú decides qué leer entero.
        </p>
      </div>
      <p className="text-xs uppercase tracking-widest text-white/60 font-medium">
        Análisis de medios · Resúmenes Gemini
      </p>
    </div>

    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-sm">
        <h1 className="font-heading text-3xl font-black tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-2 mb-8">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403 || status === 404) {
        setError("Los credenciales no son correctos.");
      } else {
        setError(formatApiError(err.response?.data?.detail, "No se pudo iniciar sesión. Inténtalo de nuevo más tarde."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Iniciar sesión" subtitle="Accede a tu feed de medios.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs uppercase tracking-wide font-semibold">Correo electrónico</Label>
          <Input id="email" type="email" data-testid="login-email" value={email}
            onChange={(e) => setEmail(e.target.value)} required className="rounded-none border-border h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-xs uppercase tracking-wide font-semibold">Contraseña</Label>
          <Input id="password" type="password" data-testid="login-password" value={password}
            onChange={(e) => setPassword(e.target.value)} required className="rounded-none border-border h-11" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" data-testid="login-submit" disabled={loading}
          className="rounded-none bg-primary hover:bg-blue-900 w-full h-11 gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          Entrar
        </Button>
      </form>
      <div className="flex items-center justify-between mt-6 text-sm">
        <Link to="/register" data-testid="go-register" className="text-primary font-semibold hover:underline">
          Crear cuenta
        </Link>
        <Link to="/forgot-password" data-testid="go-forgot" className="text-muted-foreground hover:text-foreground hover:underline">
          ¿Olvidaste tu contraseña?
        </Link>
      </div>
    </AuthShell>
  );
}

export { AuthShell };
